import api from './api';

export interface Adhesion {
    id: string;
    civilite: string;
    nom: string;
    prenom: string;
    dateNaissance: string;
    lieuNaissance: string;
    email: string;
    adressePersonnelle: string;
    telephone: string;
    fonctionTitre: string;
    etablissementStructure: string;
    secteur: string;
    adresse: string;
    wilaya: string;
    accepteAdhesion: boolean;
    statut: 'pending' | 'approved' | 'rejected' | 'validated';
    numeroMembre?: string;
    notesAdmin?: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateAdhesionDto {
    civilite?: string;
    nom: string;
    prenom: string;
    dateNaissance: string;
    lieuNaissance?: string;
    email: string;
    adressePersonnelle?: string;
    telephone: string;
    fonctionTitre?: string;
    etablissementStructure?: string;
    secteur: string;
    adresse: string;
    wilaya: string;
    accepteAdhesion?: boolean;
}

export interface AdhesionListResponse {
    data: Adhesion[];
    total: number;
    page: number;
    totalPages: number;
}

export interface AdhesionStatistics {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    validated: number;
}

export interface ValidateResponse {
    message: string;
    data: {
        code: string;
        adhesion: Adhesion;
    };
}

const adhesionsService = {
    // Public endpoint: Submit membership application
    create: async (data: CreateAdhesionDto): Promise<{ message: string; data: Adhesion }> => {
        const response = await api.post('/adhesions', data);
        return response.data;
    },

    // Adherent endpoint: Get own membership application details
    getMyAdhesion: async (): Promise<{ message: string; data: Adhesion }> => {
        const response = await api.get('/adhesions/me/info');
        return response.data;
    },

    // Public endpoint: Upload membership application PDF
    uploadBulletin: async (file: File): Promise<{ filename: string; url: string }> => {
        const formData = new FormData();
        formData.append('fichierAdhesion', file);
        const response = await api.post('/adhesions/upload-bulletin', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // Admin endpoints
    getAll: async (
        statut?: string,
        page: number = 1,
        limit: number = 10,
        search?: string
    ): Promise<AdhesionListResponse> => {
        const params = new URLSearchParams();
        if (statut) params.append('statut', statut);
        if (search) params.append('search', search);
        params.append('page', page.toString());
        params.append('limit', limit.toString());

        const response = await api.get(`/adhesions?${params.toString()}`);
        return response.data;
    },

    getById: async (id: string): Promise<Adhesion> => {
        const response = await api.get(`/adhesions/${id}`);
        return response.data;
    },

    update: async (id: string, data: Partial<Adhesion>): Promise<{ message: string; data: Adhesion }> => {
        const response = await api.patch(`/adhesions/${id}`, data);
        return response.data;
    },

    approve: async (id: string, numeroMembre?: string): Promise<{ message: string; data: Adhesion }> => {
        const response = await api.post(`/adhesions/${id}/approve`, { numeroMembre });
        return response.data;
    },

    // Validate payment and generate member account
    validate: async (id: string): Promise<ValidateResponse> => {
        const response = await api.post(`/adhesions/${id}/validate`);
        return response.data;
    },

    // Send professional credentials email to adherent
    sendCredentials: async (id: string, code: string): Promise<void> => {
        await api.post(`/adhesions/${id}/send-credentials`, { code });
    },

    reject: async (id: string, notesAdmin?: string): Promise<{ message: string; data: Adhesion }> => {
        const response = await api.post(`/adhesions/${id}/reject`, { notesAdmin });
        return response.data;
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/adhesions/${id}`);
    },

    getStatistics: async (): Promise<AdhesionStatistics> => {
        const response = await api.get('/adhesions/statistics');
        return response.data;
    },
};

export default adhesionsService;
